import { useState, useEffect } from 'react';
import { VStack, Button, Text, RadioGroup, Radio, Stack } from '@chakra-ui/react';
import type { ValidationRule } from '@/lib/sdk/workflows/types';
import type { ChoiceOption } from '@/lib/sdk/chatbot/types';

interface ChoiceTaskProps {
  description: string;
  choices: ChoiceOption[];
  validation: ValidationRule;
  onSubmit: (choiceId: string, choiceText: string) => void;
  isLoading?: boolean;
  // Controlled mode props
  controlled?: boolean;
  onChoiceChange?: (choice: ChoiceOption | null) => void;
  initialChoice?: ChoiceOption;
}

export function ChoiceTask({
  description,
  choices,
  validation,
  onSubmit,
  isLoading = false,
  controlled = false,
  onChoiceChange,
  initialChoice,
}: ChoiceTaskProps) {
  const [selectedChoice, setSelectedChoice] = useState<ChoiceOption | null>(initialChoice || null);

  // Update selection when initialChoice changes (for controlled mode)
  useEffect(() => {
    if (initialChoice !== undefined) {
      setSelectedChoice(initialChoice || null);
    }
  }, [initialChoice]);

  const handleSubmit = () => {
    if (validation.required && !selectedChoice) {
      return;
    }
    if (selectedChoice) {
      onSubmit(selectedChoice.id, selectedChoice.text);
    }
  };

  const handleChoiceSelect = (choiceId: string) => {
    const choice = choices.find(c => c.id === choiceId);
    setSelectedChoice(choice || null);
    if (controlled && onChoiceChange) {
      onChoiceChange(choice || null);
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      {!controlled && (
        <Text fontSize="lg" fontWeight="medium" color="text-primary">
          {description}
        </Text>
      )}

      <RadioGroup value={selectedChoice?.id || ''} onChange={handleChoiceSelect}>
        <Stack spacing={3}>
          {choices.map((choice) => (
            <Radio
              key={choice.id}
              value={choice.id}
              colorScheme="brand"
              borderColor="border-default"
              _hover={{ borderColor: 'border-hover' }}
            >
              <Text color="text-primary">{choice.text}</Text>
            </Radio>
          ))}
        </Stack>
      </RadioGroup>

      {!controlled && (
        <Button
          colorScheme="brand"
          onClick={handleSubmit}
          isLoading={isLoading}
          isDisabled={validation.required && !selectedChoice}
        >
          Submit
        </Button>
      )}
    </VStack>
  );
}